/**
 * The nav badges, from the pending signals (`GET /me/pending`, contracts
 * §10, T38). The signature is the contract: the tab bar, the sidebar and the
 * workout's top bar draw the dot, the "Rascunho"/"Nova" pills and the aria
 * labels from it.
 *
 * - `planDraft`: a generated plan is waiting for review (the Plano tab).
 * - `trainerUnread`: a trainer reply landed while the overlay was closed
 *   (the Personal tab).
 *
 * ── ONE READ, KEPT FRESH WHILE FOREGROUNDED ────────────────────────────────
 * {@link usePending} is the one hook over `["pending"]`: the badges and
 * Today's cards (the check-in, the draft notice, the Retrospectiva) read the
 * same cache entry. It refetches every 60 s while the app is in the
 * foreground (`refetchIntervalInBackground: false`, so it never wakes the
 * radio in the background) and once when the app comes back
 * (`onAppActiveChange`, T11's app-active signal). The writes that change
 * it — the draft workflow, the session end, "seen", the soreness answer —
 * already invalidate `["pending"]` (T38, T19).
 *
 * Signed out, it does not run: the key has no owner to answer for.
 */
import { pendingOptions } from "@repo/client/data/progress";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useApi } from "../../data/api";
import { fromQueryDef } from "../../data/query-client";
import { useSession } from "../../data/session";
import { onAppActiveChange } from "../../native";
import { navBadgesOf } from "../today/model";

export interface NavBadges {
  readonly planDraft: boolean;
  readonly trainerUnread: boolean;
}

/** How often the signals are re-read while the app is in the foreground. */
export const PENDING_POLL_MS = 60_000;

export function usePending() {
  const api = useApi();
  const { hasSession } = useSession();
  const query = useQuery({
    ...fromQueryDef(pendingOptions(api), { enabled: hasSession === true }),
    refetchInterval: PENDING_POLL_MS,
    refetchIntervalInBackground: false,
  });
  const { refetch } = query;
  const enabled = hasSession === true;
  useEffect(() => {
    if (!enabled) {
      return;
    }
    return onAppActiveChange((active) => {
      if (active) {
        refetch().catch(() => undefined);
      }
    });
  }, [enabled, refetch]);
  return query;
}

export function useNavBadges(): NavBadges {
  return navBadgesOf(usePending().data);
}
